/**
 * The setup wizard (Stage 5): a new organisation, from nothing to its first
 * onboarding in one sitting. Its admin runs it -- invite-only means the
 * console made the organisation and its first admin, and this is what that
 * admin sees first.
 *
 * Six steps, each saved as it is left, so stopping half way loses nothing:
 * the company and its sites; the departments it has; a few questions that
 * tailor the starters (core's `tailorStarters()`); its people as a list;
 * who does each department's part; and the first onboarding. Every step
 * writes through the same routes Settings, People and Templates use, so the
 * wizard is a path through the app rather than a second way to set it up.
 */

import { useMemo, useState, type ChangeEvent } from "react";
import {
  REGIMES,
  STARTER_TEMPLATES,
  STATE_NAMES,
  departmentsIn,
  type DepartmentsResponse,
  type Regime,
  type SetupResponse,
  type TailorResponse,
} from "@utuwrk/core";
import { ApiError, sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { listText } from "../lib/words.js";
import { go } from "../lib/router.js";
import { Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";
import { StaffingEditor } from "./Settings.js";

const STEPS = [
  "Organisation",
  "Departments",
  "Templates",
  "People",
  "Who does what",
  "First onboarding",
] as const;

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf("timeZone");
  } catch {
    return [];
  }
})();

const lines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

/** Every department a starter gives cards to or asks to sign off. */
const STARTER_DEPARTMENTS = [...new Set(STARTER_TEMPLATES.flatMap((template) => departmentsIn(template)))];

export function Setup() {
  const { api, org, refreshPeople } = useOrg();
  const setup = useLoad(() => api.setup(), [api]);
  const departments = useLoad(() => api.departments(), [api]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /** Run a step's writes; move on only when every one of them landed. */
  async function next(work: () => Promise<void>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      setup.setData(await api.setup());
      departments.setData(await api.departments());
      await refreshPeople();
      setStep((at) => Math.min(at + 1, STEPS.length - 1));
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  if ((!setup.data || !departments.data) && !setup.error && !departments.error)
    return <Loading title="Set up utuwrk" />;

  const props = { busy, next, back: step > 0 ? () => setStep(step - 1) : null };
  return (
    <section className="page">
      <PageHeader
        title={`Set up ${org.name}`}
        meta="From nothing to your first onboarding. Each step is saved as you leave it."
      />
      <ol className="wizard-steps" aria-label="Steps">
        {STEPS.map((title, index) => (
          <li
            key={title}
            aria-current={index === step ? "step" : undefined}
            className={index < step ? "done" : ""}
          >
            <span className="wizard-number">{index + 1}</span>
            <span className="wizard-title">{title}</span>
          </li>
        ))}
      </ol>
      <Problem>{setup.error ?? departments.error ?? problem}</Problem>
      {setup.data && departments.data ? (
        step === 0 ? (
          <Organisation setup={setup.data} {...props} />
        ) : step === 1 ? (
          <Departments list={departments.data} {...props} />
        ) : step === 2 ? (
          <Templates setup={setup.data} list={departments.data} {...props} />
        ) : step === 3 ? (
          <People {...props} />
        ) : step === 4 ? (
          <Staffing setup={setup.data} {...props} onChange={(data) => setup.setData(data)} />
        ) : (
          <FirstOnboarding back={props.back} />
        )
      ) : null}
    </section>
  );
}

interface StepProps {
  busy: boolean;
  next: (work: () => Promise<void>) => Promise<void>;
  back: (() => void) | null;
}

function StepButtons({
  busy,
  back,
  label = "Next",
  onNext,
}: Pick<StepProps, "busy" | "back"> & { label?: string; onNext: () => void }) {
  return (
    <div className="action-row section-gap">
      <button className="button" type="button" disabled={busy} onClick={onNext}>
        {label}
      </button>
      {back ? (
        <button className="button button-quiet" type="button" disabled={busy} onClick={back}>
          Back
        </button>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------- the steps -- */

function Organisation({ setup, busy, next, back }: StepProps & { setup: SetupResponse }) {
  const { api, org } = useOrg();
  const global = setup.levels.find((level) => level.kind === "global");
  const company = setup.levels.find((level) => level.kind === "company");
  const sites = setup.levels.filter((level) => level.kind === "site");
  const [companyName, setCompanyName] = useState(company?.name ?? org.name);
  const [siteText, setSiteText] = useState("");
  const [timeZone, setTimeZone] = useState(
    global?.settings.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [state, setState] = useState(global?.settings.jurisdiction ?? "");

  const save = () =>
    next(async () => {
      // Said before anything is written, so a half-saved step is never the answer to a missing site.
      if (sites.length === 0 && lines(siteText).length === 0)
        throw new ApiError("Name at least one site: every onboarding happens somewhere.", 400);
      const settings = { ...global?.settings, timeZone, ...(state ? { jurisdiction: state } : {}) };
      await api.saveLevel("global", { settings });
      let parent = company?.id;
      if (!parent) {
        const made = await api.createLevel({ kind: "company", name: companyName });
        parent = made.levels.find((level) => level.kind === "company")!.id;
      }
      const have = new Set(sites.map((site) => site.name.toLowerCase()));
      for (const name of lines(siteText)) {
        if (have.has(name.toLowerCase())) continue;
        await api.createLevel({ kind: "site", parentId: parent, name });
      }
    });

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Your organisation</h2>
      <p className="muted">
        Where people work. Each site can have its own holidays and hours later, in Settings.
      </p>
      <div className="controls">
        {company ? (
          <p className="field">
            <span>Company</span>
            <strong>{company.name}</strong>
          </p>
        ) : (
          <label className="field">
            <span>Company</span>
            <input value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
          </label>
        )}
        <label className="field">
          <span>Time zone</span>
          <select value={timeZone} onChange={(event) => setTimeZone(event.target.value)}>
            {(ZONES.includes(timeZone) ? ZONES : [timeZone, ...ZONES]).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>State, for final pay rules</span>
          <select value={state} onChange={(event) => setState(event.target.value)}>
            <option value="">Not in the US, or not now</option>
            {Object.entries(STATE_NAMES).map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {sites.length > 0 ? (
        <p>
          Sites so far: <strong>{sites.map((site) => site.name).join(", ")}</strong>. Add more below, or go
          on.
        </p>
      ) : null}
      <label className="field field-wide">
        <span>{sites.length > 0 ? "More sites" : "Sites"}, one to a line</span>
        <textarea
          rows={3}
          value={siteText}
          onChange={(event) => setSiteText(event.target.value)}
          placeholder={"Portland clinic\nTacoma warehouse"}
        />
      </label>
      <StepButtons busy={busy} back={back} onNext={() => void save()} />
    </div>
  );
}

function Departments({ list, busy, next, back }: StepProps & { list: DepartmentsResponse }) {
  const { api } = useOrg();
  const live = list.departments.filter((department) => !department.archived);
  const [kept, setKept] = useState<Set<string>>(() => new Set(live.map((department) => department.name)));
  const [more, setMore] = useState("");

  const save = () =>
    next(async () => {
      for (const department of live)
        if (!kept.has(department.name)) await api.archiveDepartment(department.name, true);
      for (const name of lines(more)) await api.addDepartment(name);
    });

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Departments</h2>
      <p className="muted">
        Untick any you do not have. The next step says who does their part instead, so no card is left with
        nobody.
      </p>
      <div className="checks">
        {live.map((department) => (
          <label key={department.name} className="check">
            <input
              type="checkbox"
              checked={kept.has(department.name)}
              onChange={(event) =>
                setKept((was) => {
                  const now = new Set(was);
                  if (event.target.checked) now.add(department.name);
                  else now.delete(department.name);
                  return now;
                })
              }
            />
            <span>{department.name}</span>
          </label>
        ))}
      </div>
      <label className="field field-wide">
        <span>Others you have, one to a line</span>
        <textarea
          rows={3}
          value={more}
          onChange={(event) => setMore(event.target.value)}
          placeholder={"Sales\nQuality"}
        />
      </label>
      <StepButtons busy={busy} back={back} onNext={() => void save()} />
    </div>
  );
}

function Templates({
  setup,
  list,
  busy,
  next,
  back,
}: StepProps & { setup: SetupResponse; list: DepartmentsResponse }) {
  const { api } = useOrg();
  const kept = list.departments
    .filter((department) => !department.archived)
    .map((department) => department.name);
  const missing = STARTER_DEPARTMENTS.filter((name) => !kept.includes(name));
  const fallback = kept.includes("HR") ? "HR" : (kept[0] ?? "");
  const [regime, setRegime] = useState<Regime>("standard");
  const [remote, setRemote] = useState(true);
  const [instead, setInstead] = useState<Record<string, string>>(() =>
    Object.fromEntries(missing.map((name) => [name, fallback])),
  );
  const [done, setDone] = useState<TailorResponse | null>(null);
  const already = setup.templates.length > 0;

  const save = () =>
    next(async () => {
      if (already) return;
      setDone(
        await api.tailor({
          tailoring: {
            regime,
            remote,
            departments: kept,
            instead: Object.fromEntries(missing.map((name) => [name, instead[name] || null])),
          },
        }),
      );
    });

  if (already)
    return (
      <div className="panel admin-panel">
        <h2 className="admin-heading">Templates</h2>
        <p>
          {setup.templates.map((template) => template.name).join(", ")}{" "}
          {setup.templates.length === 1 ? "is" : "are"} already your own. Change them any time in{" "}
          <Link to={{ name: "templates" }}>Templates</Link>.
        </p>
        {done ? (
          <ul className="facts">
            {done.templates.map((template) => (
              <li key={template.id}>
                <strong>{template.name}</strong>: {template.changes.join(" ")}
              </li>
            ))}
          </ul>
        ) : null}
        <StepButtons busy={busy} back={back} onNext={() => void save()} />
      </div>
    );

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Templates</h2>
      <p className="muted">
        A few answers make the starting templates fit. You can change any card later in Templates.
      </p>
      <fieldset className="wizard-question">
        <legend className="section-title">What your work has to follow</legend>
        <div className="checks">
          {(Object.keys(REGIMES) as Regime[]).map((option) => (
            <label key={option} className="check">
              <input
                type="radio"
                name="regime"
                checked={regime === option}
                onChange={() => setRegime(option)}
              />
              <span>{REGIMES[option]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="wizard-question">
        <legend className="section-title">Does anybody work remotely?</legend>
        <div className="checks">
          <label className="check">
            <input type="radio" name="remote" checked={remote} onChange={() => setRemote(true)} />
            <span>Yes</span>
          </label>
          <label className="check">
            <input type="radio" name="remote" checked={!remote} onChange={() => setRemote(false)} />
            <span>No: leave out home-office shipping and remote access</span>
          </label>
        </div>
      </fieldset>
      {missing.length > 0 ? (
        <div className="controls">
          {missing.map((name) => (
            <label key={name} className="field">
              <span>{name}'s part goes to</span>
              <select
                value={instead[name] ?? ""}
                onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                  setInstead((was) => ({ ...was, [name]: event.target.value }))
                }
              >
                {kept.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
                <option value="">Nobody: leave its cards out</option>
              </select>
            </label>
          ))}
        </div>
      ) : null}
      <StepButtons busy={busy} back={back} onNext={() => void save()} />
    </div>
  );
}

function People({ busy, next, back }: StepProps) {
  const { api } = useOrg();
  const [text, setText] = useState("");
  const [result, setResult] = useState<string | null>(null);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) setText(await file.text());
  }

  const save = () =>
    next(async () => {
      if (!text.trim()) return;
      const added = await api.addPeople(text);
      setResult(
        `Added ${added.added}${added.already ? `; ${added.already} were already here` : ""}. They can sign in now.`,
      );
      setText("");
    });

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">People</h2>
      <p className="muted">
        Everybody who does a part: one to a line, as name, address, department and role (admin, HR, manager,
        member or compliance; blank is member). Paste from a spreadsheet, or upload a CSV. You can skip this
        and add people later.
      </p>
      <label className="field field-wide">
        <span>People</span>
        <textarea
          rows={8}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={"Ivan Petrov, ivan@example.com, IT, member\nHana Ito, hana@example.com, HR, hr"}
        />
      </label>
      <label className="field">
        <span>Or upload a CSV</span>
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(event) => void upload(event)} />
      </label>
      {result ? (
        <p className="pill s-sent" role="status">
          {result}
        </p>
      ) : null}
      <StepButtons
        busy={busy}
        back={back}
        label={text.trim() ? "Add them and go on" : "Next"}
        onNext={() => void save()}
      />
    </div>
  );
}

function Staffing({
  setup,
  busy,
  next,
  back,
  onChange,
}: StepProps & { setup: SetupResponse; onChange: (data: SetupResponse) => void }) {
  const { api, people } = useOrg();
  const [saving, setSaving] = useState(false);
  const sites = setup.levels.filter((level) => level.kind === "site");
  /**
   * Each department's obvious person: its manager, or its only member. A
   * department with several and no manager is left for somebody to choose.
   */
  const obvious = (department: string): string | null => {
    const inIt = people.filter((person) => person.active && person.department === department);
    const managers = inIt.filter((person) => person.role === "manager");
    return managers[0]?.id ?? (inIt.length === 1 ? inIt[0]!.id : null);
  };
  const gaps = sites.flatMap((site) =>
    setup.departments
      .filter((department) => !setup.staffing[site.id]?.[department]?.primary && obvious(department))
      .map((department) => ({ siteId: site.id, department, primary: obvious(department)! })),
  );
  const [problem, setProblem] = useState<string | null>(null);
  const run = useMemo(
    () => async (work: () => Promise<SetupResponse>) => {
      setSaving(true);
      setProblem(null);
      try {
        onChange(await work());
        return true;
      } catch (failure) {
        setProblem(sentence(failure));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [onChange],
  );
  return (
    <>
      {gaps.length > 0 ? (
        <div className="panel admin-panel">
          <p>
            {listText([...new Set(gaps.map((gap) => gap.department))])} each have somebody obvious to do their
            part: their manager, or the only person in them.
          </p>
          <div className="action-row">
            <button
              className="button"
              type="button"
              disabled={busy || saving}
              onClick={() =>
                void run(async () => {
                  let latest = setup;
                  for (const gap of gaps) latest = await api.setStaffing({ ...gap, backup: null });
                  return latest;
                })
              }
            >
              Fill them in
            </button>
          </div>
        </div>
      ) : null}
      <StaffingEditor setup={setup} editable busy={busy || saving} run={run} />
      <Problem>{problem}</Problem>
      <StepButtons busy={busy || saving} back={back} onNext={() => void next(async () => undefined)} />
    </>
  );
}

function FirstOnboarding({ back }: { back: (() => void) | null }) {
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Your first onboarding</h2>
      <p>
        That is the setup. Start somebody's onboarding and every department hears when its part is ready. Ten
        people on the same day? Choose "Several people".
      </p>
      <div className="action-row">
        <button className="button" type="button" onClick={() => go({ name: "start" })}>
          Start the first onboarding
        </button>
        {back ? (
          <button className="button button-quiet" type="button" onClick={back}>
            Back
          </button>
        ) : null}
      </div>
    </div>
  );
}
